import { customFetch } from '../../services/apiService';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  CheckCircle2, XCircle, Clock, Coins, RefreshCw, Loader2, 
  Search, ShieldCheck, Filter, FileText, UserCheck, AlertCircle,
  Plus, Edit3, Trash2, Package, Tag, Check, X, ToggleLeft, ToggleRight,
  Layers, ArrowUpRight, CreditCard, Building2, QrCode, Crown, Gem,
  Copy, CheckCheck, Ticket, Percent, Calendar
} from 'lucide-react';
import CustomDialog from '../CustomDialog';
import { useDebounce } from '../../hooks/useDebounce';
import { ServerPagination } from '../ui/ServerPagination';
import { 
  useAdminTopUpRequests, 
  useAdminTopUpPackages, 
  useAdminBankInfo, 
  useAdminCoupons,
  useAdminApproveRequestMutation,
  useAdminRejectRequestMutation,
  useAdminSavePackageMutation,
  useAdminDeletePackageMutation,
  useAdminSaveBankInfoMutation,
  useAdminSaveCouponMutation,
  useAdminDeleteCouponMutation,
  useAdminToggleCouponMutation
} from '../../hooks/useQueries';
import { useQueryClient } from '@tanstack/react-query';

interface TopUpRequest {
  id: string;
  _id?: string;
  paymentCode?: string;
  packageName?: string;
  packageType?: 'XU' | 'VIP' | 'SVIP';
  durationDays?: number;
  userId: string;
  userEmail: string;
  userName: string;
  amountMoney: number;
  amountVnd?: number;
  creditsToGain: number;
  credits?: number;
  transferContent: string;
  status: 'WAITING_PAYMENT' | 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  processedAt?: string;
}

interface TopUpPackage {
  id: string;
  packageId?: string;
  name: string;
  price: number;
  credits: number;
  description: string;
  badgeText?: string;
  isActive: boolean;
  order: number;
  type?: 'XU' | 'VIP' | 'SVIP';
  durationDays?: number;
  maxQuantity?: number;
  soldQuantity?: number;
}

interface BankConfig {
  bankName: string;
  bankId: string;
  accountNo: string;
  accountName: string;
}

interface TopUpCoupon {
  id: string;
  _id?: string;
  code: string;
  discountPercent: number;
  usageLimit: number;
  usageCount: number;
  expiresAt: string | null;
  isActive: boolean;
  description?: string;
  createdAt: string;
  updatedAt?: string;
}

// Popular VietQR Banks for Quick Selection
const POPULAR_BANKS = [
  { bankName: 'MBBank (Ngân hàng Quân Đội)', bankId: 'MB' },
  { bankName: 'Vietcombank (VCB)', bankId: 'VCB' },
  { bankName: 'Techcombank (TCB)', bankId: 'TCB' },
  { bankName: 'BIDV', bankId: 'BIDV' },
  { bankName: 'VietinBank (ICB)', bankId: 'ICB' },
  { bankName: 'VPBank', bankId: 'VPB' },
  { bankName: 'ACB (Á Châu)', bankId: 'ACB' },
  { bankName: 'Sacombank', bankId: 'STB' },
  { bankName: 'TPBank', bankId: 'TPB' },
  { bankName: 'Agribank', bankId: 'VAR' },
];

export const TopUpApproval: React.FC = () => {
  const queryClient = useQueryClient();

  // Main Sub-Tab State
  const [activeSubTab, setActiveSubTab] = useState<'requests' | 'packages' | 'bank' | 'coupons'>('requests');

  // Read initial URL params for requests pagination & filters
  const getInitialRequestParams = () => {
    if (typeof window === 'undefined') {
      return { page: 1, limit: 10, search: '', status: 'ALL', type: 'ALL' };
    }
    const searchParams = new URLSearchParams(window.location.search);
    return {
      page: parseInt(searchParams.get('page') || searchParams.get('order_page') || '1', 10) || 1,
      limit: parseInt(searchParams.get('limit') || searchParams.get('order_limit') || '10', 10) || 10,
      search: searchParams.get('search') || searchParams.get('order_search') || '',
      status: searchParams.get('status') || searchParams.get('order_status') || 'ALL',
      type: searchParams.get('type') || searchParams.get('order_type') || 'ALL'
    };
  };

  const initialParams = useMemo(() => getInitialRequestParams(), []);

  // --- SUB-TAB 1: REQUESTS STATE (Server-side Pagination & Filtering) ---
  const [currentPage, setCurrentPage] = useState<number>(initialParams.page);
  const [pageSize, setPageSize] = useState<number>(initialParams.limit);
  const [searchTerm, setSearchTerm] = useState<string>(initialParams.search);
  const [filterStatus, setFilterStatus] = useState<string>(initialParams.status);
  const [filterType, setFilterType] = useState<string>(initialParams.type);

  // Debounce search input 500ms
  const debouncedSearch = useDebounce(searchTerm, 500);

  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);

  // Bulk request selection state
  const [selectedRequestIds, setSelectedRequestIds] = useState<string[]>([]);
  const [bulkActionType, setBulkActionType] = useState<'approve' | 'reject' | null>(null);
  const [bulkAdminNote, setBulkAdminNote] = useState<string>('');
  const [isProcessingBulk, setIsProcessingBulk] = useState<boolean>(false);

  // Format Date to DD/MM/YYYY HH:mm
  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '--';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const pad = (n: number) => n.toString().padStart(2, '0');
      const day = pad(d.getDate());
      const month = pad(d.getMonth() + 1);
      const year = d.getFullYear();
      const hours = pad(d.getHours());
      const minutes = pad(d.getMinutes());
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch {
      return dateStr;
    }
  };

  // Format Date to DD/MM/YYYY
  const formatDateOnly = (dateStr?: string | null) => {
    if (!dateStr) return 'Vô thời hạn';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const pad = (n: number) => n.toString().padStart(2, '0');
      const day = pad(d.getDate());
      const month = pad(d.getMonth() + 1);
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return dateStr;
    }
  };

  // Copy helper
  const handleCopy = (text?: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedCode(text);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // --- SUB-TAB 2: PACKAGES STATE ---
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState<TopUpPackage | null>(null);
  const [isSavingPackage, setIsSavingPackage] = useState(false);

  // Package Form state
  const [pkgFormData, setPkgFormData] = useState({
    name: '',
    price: 50000,
    credits: 500,
    badgeText: '',
    description: '',
    isActive: true,
    order: 1,
    type: 'XU' as 'XU' | 'VIP' | 'SVIP',
    durationDays: 0,
    maxQuantity: 0
  });

  // --- SUB-TAB 3: BANK CONFIG STATE ---
  const [bankConfig, setBankConfig] = useState<BankConfig>({
    bankName: 'MBBank (Ngân hàng Quân Đội)',
    bankId: 'MB',
    accountNo: '0388888888',
    accountName: 'VO THI MINH HAN'
  });
  const [isSavingBankInfo, setIsSavingBankInfo] = useState(false);

  // --- SUB-TAB 4: COUPONS STATE ---
  const [couponSearch, setCouponSearch] = useState<string>('');
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<TopUpCoupon | null>(null);
  const [isSavingCoupon, setIsSavingCoupon] = useState(false);
  const [togglingCouponId, setTogglingCouponId] = useState<string | null>(null);

  // Coupon Form state
  const [couponFormData, setCouponFormData] = useState({
    code: '',
    discountPercent: 10,
    usageLimit: 0,
    expiresAt: '',
    isActive: true,
    description: ''
  });

  // Custom Dialog
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    type?: 'success' | 'error' | 'warning' | 'confirm';
    title: string;
    message: string;
    onConfirm?: () => void;
  }>({
    isOpen: false,
    title: '',
    message: ''
  });

  const isMountedRef = useRef<boolean>(false);

  // React Query: Requests
  const {
    data: requestsQueryData,
    isLoading: loadingRequests,
    isFetching: isFetchingRequests,
    refetch: refetchRequests
  } = useAdminTopUpRequests({
    page: currentPage,
    limit: pageSize,
    search: debouncedSearch,
    status: filterStatus,
    type: filterType
  });

  const requests: TopUpRequest[] = requestsQueryData?.data || [];
  const totalRecords = requestsQueryData?.pagination?.totalCount ?? 0;
  const totalPages = requestsQueryData?.pagination?.totalPages ?? 1;

  // React Query: Packages
  const {
    data: packages = [],
    isLoading: loadingPackages,
    refetch: fetchPackages
  } = useAdminTopUpPackages();

  // React Query: Bank Info
  const {
    data: fetchedBankInfo,
    isLoading: loadingBankInfo,
    refetch: fetchBankInfo
  } = useAdminBankInfo();

  useEffect(() => {
    if (fetchedBankInfo) {
      setBankConfig({
        bankName: fetchedBankInfo.bankName || 'MBBank',
        bankId: fetchedBankInfo.bankId || 'MB',
        accountNo: fetchedBankInfo.accountNo || '',
        accountName: fetchedBankInfo.accountName || ''
      });
    }
  }, [fetchedBankInfo]);

  // React Query: Coupons
  const {
    data: coupons = [],
    isLoading: loadingCoupons,
    refetch: fetchCoupons
  } = useAdminCoupons();

  const fetchRequests = () => {
    queryClient.invalidateQueries({ queryKey: ['adminTopUpRequests'] });
  };

  // URL Sync when on requests tab
  useEffect(() => {
    if (typeof window !== 'undefined' && activeSubTab === 'requests') {
      const url = new URL(window.location.href);

      // Clean up legacy order_* redundant params if present
      url.searchParams.delete('order_page');
      url.searchParams.delete('order_limit');
      url.searchParams.delete('order_search');
      url.searchParams.delete('order_status');
      url.searchParams.delete('order_type');

      if (currentPage > 1) {
        url.searchParams.set('page', String(currentPage));
      } else {
        url.searchParams.delete('page');
      }

      if (pageSize !== 10) {
        url.searchParams.set('limit', String(pageSize));
      } else {
        url.searchParams.delete('limit');
      }

      if (debouncedSearch.trim()) {
        url.searchParams.set('search', debouncedSearch.trim());
      } else {
        url.searchParams.delete('search');
      }

      if (filterStatus !== 'ALL') {
        url.searchParams.set('status', filterStatus);
      } else {
        url.searchParams.delete('status');
      }

      if (filterType !== 'ALL') {
        url.searchParams.set('type', filterType);
      } else {
        url.searchParams.delete('type');
      }

      window.history.replaceState(null, '', url.toString());
    }
  }, [currentPage, pageSize, debouncedSearch, filterStatus, filterType, activeSubTab]);

  // Reset page to 1 when filters or search change
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    setCurrentPage(1);
  }, [debouncedSearch, filterStatus, filterType]);

  // Save Bank Info Config
  const handleSaveBankInfo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bankConfig.bankName.trim() || !bankConfig.bankId.trim() || !bankConfig.accountNo.trim() || !bankConfig.accountName.trim()) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'Thông tin chưa đầy đủ',
        message: 'Vui lòng điền đầy đủ Tên ngân hàng, Mã VietQR Bank ID, Số tài khoản và Tên chủ tài khoản.'
      });
      return;
    }

    setIsSavingBankInfo(true);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
      const res = await customFetch('/api/topup/admin/bank-info', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bankConfig)
      });

      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ['adminBankInfo'] });
        queryClient.invalidateQueries({ queryKey: ['bankInfo'] });
        setDialog({
          isOpen: true,
          type: 'success',
          title: 'Lưu thành công',
          message: 'Đã cập nhật thông tin Ngân hàng nhận tiền thành công!'
        });
      } else {
        const err = await res.json();
        setDialog({
          isOpen: true,
          type: 'error',
          title: 'Lỗi lưu thông tin',
          message: err.message || 'Không thể lưu thông tin ngân hàng.'
        });
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'Lỗi kết nối',
        message: 'Đã xảy ra lỗi khi kết nối máy chủ.'
      });
    } finally {
      setIsSavingBankInfo(false);
    }
  };

  // Approve TopUp Request
  const handleApprove = (req: TopUpRequest) => {
    const safeCredits = req?.creditsToGain ?? (req as any)?.credits ?? 0;
    const code = req.paymentCode || req.transferContent || req.id;
    const pkgType = req.packageType || (req as any)?.type || (
      req.packageName?.includes('SVIP') ? 'SVIP' :
      req.packageName?.includes('VIP') ? 'VIP' : 'XU'
    );
    const actionDesc = pkgType === 'SVIP' 
      ? `kích hoạt gói SVIP và cộng ${safeCredits} Xu`
      : pkgType === 'VIP'
      ? `kích hoạt gói VIP và cộng ${safeCredits} Xu`
      : `cộng ${safeCredits.toLocaleString('vi-VN')} Xu`;

    setDialog({
      isOpen: true,
      type: 'confirm',
      title: 'Xác nhận duyệt thanh toán',
      message: `Bạn có chắc chắn muốn duyệt đơn #${code} và ${actionDesc} cho ${req.userName} (${req.userEmail})?`,
      onConfirm: async () => {
        setDialog(prev => ({ ...prev, isOpen: false }));
        setProcessingId(req.id);
        try {
          const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
          const res = await customFetch(`/api/topup/admin/requests/${req.id}/approve`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            }
          });

          if (res.ok) {
            const data = await res.json();
            fetchRequests();
            setDialog({
              isOpen: true,
              type: 'success',
              title: 'Duyệt thành công',
              message: data.message || `Đã duyệt đơn #${code} thành công!`
            });
          } else {
            const err = await res.json();
            setDialog({
              isOpen: true,
              type: 'error',
              title: 'Lỗi',
              message: err.message || 'Không thể phê duyệt đơn hàng.'
            });
          }
        } catch (err) {
          setDialog({
            isOpen: true,
            type: 'error',
            title: 'Lỗi kết nối',
            message: 'Đã xảy ra lỗi khi phê duyệt đơn hàng.'
          });
        } finally {
          setProcessingId(null);
        }
      }
    });
  };

  // Reject TopUp Request
  const handleReject = (req: TopUpRequest) => {
    const code = req.paymentCode || req.transferContent || req.id;
    setDialog({
      isOpen: true,
      type: 'confirm',
      title: 'Xác nhận từ chối đơn hàng',
      message: `Bạn có chắc chắn muốn từ chối đơn hàng #${code} của ${req.userName}?`,
      onConfirm: async () => {
        setDialog(prev => ({ ...prev, isOpen: false }));
        setProcessingId(req.id);
        try {
          const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
          const res = await customFetch(`/api/topup/admin/requests/${req.id}/reject`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ reason: 'Admin từ chối giao dịch' })
          });

          if (res.ok) {
            fetchRequests();
            setDialog({
              isOpen: true,
              type: 'success',
              title: 'Đã từ chối',
              message: `Đã từ chối đơn hàng #${code}.`
            });
          } else {
            const err = await res.json();
            setDialog({
              isOpen: true,
              type: 'error',
              title: 'Lỗi',
              message: err.message || 'Không thể từ chối đơn hàng.'
            });
          }
        } catch (err) {
          setDialog({
            isOpen: true,
            type: 'error',
            title: 'Lỗi kết nối',
            message: 'Đã xảy ra lỗi khi kết nối máy chủ.'
          });
        } finally {
          setProcessingId(null);
        }
      }
    });
  };

  // Execute Bulk Action (Approve or Reject)
  const handleExecuteBulkAction = async () => {
    if (!bulkActionType || selectedRequestIds.length === 0) return;
    setIsProcessingBulk(true);

    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
      const endpoint = bulkActionType === 'approve'
        ? '/api/topup/admin/requests/bulk-approve'
        : '/api/topup/admin/requests/bulk-reject';

      const res = await customFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          requestIds: selectedRequestIds,
          adminNote: bulkAdminNote.trim()
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        const count = selectedRequestIds.length;
        const currentType = bulkActionType;
        setBulkActionType(null);
        setBulkAdminNote('');
        setSelectedRequestIds([]);
        await fetchRequests();

        setDialog({
          isOpen: true,
          type: 'success',
          title: currentType === 'approve' ? 'Duyệt Hàng Loạt Thành Công' : 'Từ Chối Hàng Loạt Thành Công',
          message: data.message || `Đã ${currentType === 'approve' ? 'duyệt' : 'từ chối'} thành công ${count} đơn hàng được chọn.`
        });
      } else {
        setDialog({
          isOpen: true,
          type: 'error',
          title: 'Lỗi thao tác hàng loạt',
          message: data.message || 'Không thể xử lý các đơn hàng đã chọn.'
        });
      }
    } catch (err) {
      console.error("Lỗi thao tác hàng loạt:", err);
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'Lỗi kết nối',
        message: 'Đã xảy ra lỗi khi gửi yêu cầu xử lý hàng loạt.'
      });
    } finally {
      setIsProcessingBulk(false);
    }
  };

  // Open Package Modal for Create/Edit
  const handleOpenPackageModal = (pkg?: TopUpPackage) => {
    if (pkg) {
      setEditingPackage(pkg);
      setPkgFormData({
        name: pkg.name,
        price: pkg.price ?? 0,
        credits: pkg.credits ?? 0,
        badgeText: pkg.badgeText || '',
        description: pkg.description || '',
        isActive: pkg.isActive,
        order: pkg.order || 1,
        type: (pkg.type === 'VIP' ? 'VIP' : pkg.type === 'SVIP' ? 'SVIP' : 'XU'),
        durationDays: pkg.durationDays ?? 0,
        maxQuantity: pkg.maxQuantity ?? 0
      });
    } else {
      setEditingPackage(null);
      setPkgFormData({
        name: '',
        price: 100000,
        credits: 1000,
        badgeText: '',
        description: '',
        isActive: true,
        order: packages.length + 1,
        type: 'XU',
        durationDays: 0,
        maxQuantity: 0
      });
    }
    setShowPackageModal(true);
  };

  // Toggle Package Active Status
  const handleTogglePackageActive = async (pkg: TopUpPackage) => {
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
      const pkgId = pkg.id || pkg.packageId;
      const res = await customFetch(`/api/topup/admin/packages/${pkgId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ isActive: !pkg.isActive })
      });

      if (res.ok) {
        fetchPackages();
      } else {
        const err = await res.json();
        setDialog({
          isOpen: true,
          type: 'error',
          title: 'Lỗi',
          message: err.message || 'Không thể thay đổi trạng thái gói.'
        });
      }
    } catch (err) {
      console.error('Lỗi thay đổi trạng thái gói:', err);
    }
  };

  // Delete Package
  const handleDeletePackage = (pkg: TopUpPackage) => {
    setDialog({
      isOpen: true,
      type: 'confirm',
      title: 'Xác nhận xóa Gói Xu',
      message: `Bạn có chắc chắn muốn xóa "${pkg.name}"? Hành động này không thể hoàn tác.`,
      onConfirm: async () => {
        setDialog(prev => ({ ...prev, isOpen: false }));
        try {
          const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
          const pkgId = pkg.id || pkg.packageId;
          const res = await customFetch(`/api/topup/admin/packages/${pkgId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (res.ok) {
            fetchPackages();
            setDialog({
              isOpen: true,
              type: 'success',
              title: 'Đã xóa',
              message: `Đã xóa thành công gói Xu "${pkg.name}".`
            });
          } else {
            const err = await res.json();
            setDialog({
              isOpen: true,
              type: 'error',
              title: 'Lỗi',
              message: err.message || 'Không thể xóa gói Xu.'
            });
          }
        } catch (err) {
          setDialog({
            isOpen: true,
            type: 'error',
            title: 'Lỗi kết nối',
            message: 'Đã xảy ra lỗi khi xóa gói Xu.'
          });
        }
      }
    });
  };

  // Save Package (Create or Edit)
  const handleSavePackage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pkgFormData.name.trim() || pkgFormData.price < 0) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'Thông tin chưa hợp lệ',
        message: 'Vui lòng điền đầy đủ Tên gói và Giá tiền VNĐ (>= 0).'
      });
      return;
    }

    if (pkgFormData.type === 'XU' && pkgFormData.credits <= 0) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'Thông tin chưa hợp lệ',
        message: 'Gói Xu yêu cầu số Xu nhận phải lớn hơn 0.'
      });
      return;
    }

    if ((pkgFormData.type === 'VIP' || pkgFormData.type === 'SVIP') && (!pkgFormData.durationDays || pkgFormData.durationDays <= 0)) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'Thông tin chưa hợp lệ',
        message: 'Gói VIP / SVIP yêu cầu Số ngày hiệu lực phải lớn hơn 0.'
      });
      return;
    }

    setIsSavingPackage(true);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
      const isEdit = !!editingPackage;
      const pkgId = editingPackage ? (editingPackage.id || editingPackage.packageId) : '';
      const url = isEdit ? `/api/topup/admin/packages/${pkgId}` : '/api/topup/admin/packages';
      const method = isEdit ? 'PUT' : 'POST';

      const payload = {
        name: pkgFormData.name.trim(),
        price: Number(pkgFormData.price),
        credits: Number(pkgFormData.credits || 0),
        badgeText: pkgFormData.badgeText ? pkgFormData.badgeText.trim() : '',
        description: pkgFormData.description ? pkgFormData.description.trim() : '',
        isActive: Boolean(pkgFormData.isActive),
        order: Number(pkgFormData.order || 1),
        type: pkgFormData.type === 'VIP' ? 'VIP' : pkgFormData.type === 'SVIP' ? 'SVIP' : 'XU',
        durationDays: pkgFormData.type === 'XU' ? 0 : Number(pkgFormData.durationDays || 0),
        maxQuantity: Math.max(0, Number(pkgFormData.maxQuantity || 0))
      };

      const res = await customFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setShowPackageModal(false);
        fetchPackages();
        setDialog({
          isOpen: true,
          type: 'success',
          title: isEdit ? 'Cập nhật thành công' : 'Thêm gói mới thành công',
          message: isEdit ? `Đã cập nhật gói "${pkgFormData.name}".` : `Đã thêm thành công gói "${pkgFormData.name}".`
        });
      } else {
        const err = await res.json();
        setDialog({
          isOpen: true,
          type: 'error',
          title: 'Lỗi lưu thông tin',
          message: err.message || 'Không thể lưu thông tin Gói.'
        });
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'Lỗi kết nối',
        message: 'Không thể kết nối máy chủ để lưu Gói.'
      });
    } finally {
      setIsSavingPackage(false);
    }
  };

  // --- SUB-TAB 4: COUPON ACTION HANDLERS ---
  
  // Open Coupon Modal for Create/Edit
  const handleOpenCouponModal = (coupon?: TopUpCoupon) => {
    if (coupon) {
      setEditingCoupon(coupon);
      let expDateStr = '';
      if (coupon.expiresAt) {
        try {
          const d = new Date(coupon.expiresAt);
          if (!isNaN(d.getTime())) {
            expDateStr = d.toISOString().split('T')[0];
          }
        } catch {}
      }
      setCouponFormData({
        code: coupon.code,
        discountPercent: coupon.discountPercent ?? 10,
        usageLimit: coupon.usageLimit ?? 0,
        expiresAt: expDateStr,
        isActive: coupon.isActive ?? true,
        description: coupon.description || ''
      });
    } else {
      setEditingCoupon(null);
      setCouponFormData({
        code: '',
        discountPercent: 10,
        usageLimit: 0,
        expiresAt: '',
        isActive: true,
        description: ''
      });
    }
    setShowCouponModal(true);
  };

  // Toggle Coupon Active Status
  const handleToggleCouponActive = async (coupon: TopUpCoupon) => {
    const couponId = coupon.id || coupon._id;
    if (!couponId) return;
    setTogglingCouponId(couponId);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
      const res = await customFetch(`/api/topup/admin/coupons/${couponId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ isActive: !coupon.isActive })
      });

      if (res.ok) {
        fetchCoupons();
      } else {
        const err = await res.json();
        setDialog({
          isOpen: true,
          type: 'error',
          title: 'Lỗi',
          message: err.message || 'Không thể thay đổi trạng thái mã giảm giá.'
        });
      }
    } catch (err) {
      console.error('Lỗi thay đổi trạng thái coupon:', err);
    } finally {
      setTogglingCouponId(null);
    }
  };

  // Delete Coupon
  const handleDeleteCoupon = (coupon: TopUpCoupon) => {
    setDialog({
      isOpen: true,
      type: 'confirm',
      title: 'Xác nhận xóa Mã Giảm Giá',
      message: `Bạn có chắc chắn muốn xóa mã "${coupon.code}" (-${coupon.discountPercent}%)? Hành động này không thể hoàn tác.`,
      onConfirm: async () => {
        setDialog(prev => ({ ...prev, isOpen: false }));
        try {
          const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
          const couponId = coupon.id || coupon._id;
          const res = await customFetch(`/api/topup/admin/coupons/${couponId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (res.ok) {
            fetchCoupons();
            setDialog({
              isOpen: true,
              type: 'success',
              title: 'Đã xóa',
              message: `Đã xóa thành công mã giảm giá "${coupon.code}".`
            });
          } else {
            const err = await res.json();
            setDialog({
              isOpen: true,
              type: 'error',
              title: 'Lỗi',
              message: err.message || 'Không thể xóa mã giảm giá.'
            });
          }
        } catch (err) {
          setDialog({
            isOpen: true,
            type: 'error',
            title: 'Lỗi kết nối',
            message: 'Đã xảy ra lỗi khi kết nối máy chủ.'
          });
        }
      }
    });
  };

  // Save Coupon (Create or Edit)
  const handleSaveCoupon = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanCode = couponFormData.code.trim().toUpperCase();
    if (!cleanCode) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'Thông tin chưa hợp lệ',
        message: 'Vui lòng nhập Mã giảm giá.'
      });
      return;
    }

    const discountNum = Number(couponFormData.discountPercent);
    if (isNaN(discountNum) || discountNum < 0 || discountNum > 100) {
      setDialog({
        isOpen: true,
        type: 'warning',
        title: 'Thông tin chưa hợp lệ',
        message: 'Phần trăm giảm giá phải từ 0% đến 100%.'
      });
      return;
    }

    setIsSavingCoupon(true);
    try {
      const token = typeof window !== 'undefined' ? localStorage.getItem('nha_giao_so_sso_token') || '' : '';
      const isEdit = !!editingCoupon;
      const couponId = editingCoupon ? (editingCoupon.id || editingCoupon._id) : '';
      const url = isEdit ? `/api/topup/admin/coupons/${couponId}` : '/api/topup/admin/coupons';
      const method = isEdit ? 'PUT' : 'POST';

      const payload = {
        code: cleanCode,
        discountPercent: discountNum,
        usageLimit: Math.max(0, Number(couponFormData.usageLimit) || 0),
        expiresAt: couponFormData.expiresAt ? new Date(couponFormData.expiresAt + 'T23:59:59').toISOString() : null,
        isActive: Boolean(couponFormData.isActive),
        description: couponFormData.description ? couponFormData.description.trim() : ''
      };

      const res = await customFetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setShowCouponModal(false);
        fetchCoupons();
        setDialog({
          isOpen: true,
          type: 'success',
          title: isEdit ? 'Cập nhật thành công' : 'Tạo mã mới thành công',
          message: isEdit ? `Đã cập nhật mã giảm giá "${cleanCode}".` : `Đã tạo mã giảm giá "${cleanCode}" (-${discountNum}%).`
        });
      } else {
        const err = await res.json();
        setDialog({
          isOpen: true,
          type: 'error',
          title: 'Lỗi lưu thông tin',
          message: err.message || 'Không thể lưu mã giảm giá.'
        });
      }
    } catch (err) {
      setDialog({
        isOpen: true,
        type: 'error',
        title: 'Lỗi kết nối',
        message: 'Không thể kết nối máy chủ để lưu mã giảm giá.'
      });
    } finally {
      setIsSavingCoupon(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Sub-tab Navigation */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-2">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={() => setActiveSubTab('requests')}
            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center space-x-2 transition-all ${
              activeSubTab === 'requests'
                ? 'bg-amber-500 text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <Coins className="w-4 h-4" />
            <span>Danh sách đơn hàng</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSubTab('packages')}
            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center space-x-2 transition-all ${
              activeSubTab === 'packages'
                ? 'bg-amber-500 text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <Package className="w-4 h-4" />
            <span>Cài đặt Gói Xu</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSubTab('bank')}
            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center space-x-2 transition-all ${
              activeSubTab === 'bank'
                ? 'bg-amber-500 text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <Building2 className="w-4 h-4" />
            <span>Cài đặt Ngân hàng</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveSubTab('coupons')}
            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center space-x-2 transition-all ${
              activeSubTab === 'coupons'
                ? 'bg-amber-500 text-white shadow-xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            <Tag className="w-4 h-4" />
            <span>Cài đặt Coupon</span>
          </button>
        </div>

        {activeSubTab === 'packages' && (
          <button
            type="button"
            onClick={() => handleOpenPackageModal()}
            className="px-3.5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center space-x-1.5"
          >
            <Plus className="w-4 h-4" />
            <span>Thêm Gói Xu mới</span>
          </button>
        )}

        {activeSubTab === 'coupons' && (
          <button
            type="button"
            onClick={() => handleOpenCouponModal()}
            className="px-3.5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center space-x-1.5 cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Thêm Mã Giảm Giá</span>
          </button>
        )}
      </div>

      {/* ==================== SUB-TAB 1: DANH SÁCH ĐƠN HÀNG (QUẢN LÝ THANH TOÁN) ==================== */}
      {activeSubTab === 'requests' && (() => {
        const isAllSelected = requests.length > 0 && requests.every(r => selectedRequestIds.includes(r.id || (r as any)._id));
        const handleSelectAll = () => {
          if (isAllSelected) {
            const pageIds = new Set(requests.map(r => r.id || (r as any)._id));
            setSelectedRequestIds(prev => prev.filter(id => !pageIds.has(id)));
          } else {
            const pageIds = requests.map(r => r.id || (r as any)._id).filter(Boolean);
            setSelectedRequestIds(prev => Array.from(new Set([...prev, ...pageIds])));
          }
        };
        const handleToggleSelectRow = (id: string) => {
          setSelectedRequestIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
        };

        return (
          <div className="space-y-4">
            {/* Header Banner */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-base font-extrabold text-slate-900 flex items-center space-x-2">
                  <Coins className="w-5 h-5 text-amber-500" />
                  <span>Quản lý Thanh toán</span>
                  <span className="text-xs font-bold px-2.5 py-0.5 bg-amber-100 text-amber-800 rounded-full">
                    {totalRecords} đơn hàng
                  </span>
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Theo dõi giao dịch, xác nhận chuyển khoản và quản lý đơn nạp / nâng cấp VIP
                </p>
              </div>

              {/* Status summary badges */}
              <div className="flex items-center space-x-2 text-xs">
                <span className="px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200/80 rounded-xl font-bold">
                  {requests.filter(r => r.status === 'PENDING').length} Chờ duyệt trên trang
                </span>
                <span className="px-2.5 py-1 bg-slate-100 text-slate-700 border border-slate-200 rounded-xl font-bold">
                  {requests.filter(r => r.status === 'WAITING_PAYMENT').length} Chờ thanh toán
                </span>
              </div>
            </div>

            {/* Toolbar (Filters & Search) */}
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
              {/* Search Bar */}
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Tìm kiếm theo Mã đơn (paymentCode) hoặc Email người dùng..."
                  className="w-full pl-9.5 pr-8 py-2 bg-slate-50 hover:bg-slate-100/80 focus:bg-white border border-slate-200 focus:border-amber-500 rounded-xl text-xs text-slate-800 placeholder-slate-400 outline-none transition-all"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600 rounded-full"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Filters & Actions */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Status Filter Dropdown */}
                <div className="flex items-center space-x-1 bg-slate-50 border border-slate-200 rounded-xl px-2 py-1">
                  <Filter className="w-3.5 h-3.5 text-slate-400 ml-1" />
                  <select
                    value={filterStatus}
                    onChange={e => setFilterStatus(e.target.value)}
                    className="bg-transparent py-1 px-1.5 text-xs font-bold text-slate-700 outline-none cursor-pointer"
                  >
                    <option value="ALL">Tất cả trạng thái</option>
                    <option value="WAITING_PAYMENT">Chờ thanh toán (WAITING_PAYMENT)</option>
                    <option value="PENDING">Chờ duyệt (PENDING)</option>
                    <option value="APPROVED">Đã duyệt (APPROVED)</option>
                    <option value="REJECTED">Từ chối (REJECTED)</option>
                  </select>
                </div>

                {/* Transaction Type Filter Dropdown */}
                <div className="flex items-center space-x-1 bg-slate-50 border border-slate-200 rounded-xl px-2 py-1">
                  <Tag className="w-3.5 h-3.5 text-slate-400 ml-1" />
                  <select
                    value={filterType}
                    onChange={e => setFilterType(e.target.value)}
                    className="bg-transparent py-1 px-1.5 text-xs font-bold text-slate-700 outline-none cursor-pointer"
                  >
                    <option value="ALL">Tất cả loại giao dịch</option>
                    <option value="XU">Nạp Xu (XU)</option>
                    <option value="VIP">Nâng VIP (VIP)</option>
                    <option value="SVIP">Nâng SVIP (SVIP)</option>
                  </select>
                </div>

                {/* Refresh Button */}
                <button
                  type="button"
                  onClick={() => fetchRequests()}
                  className="p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors shrink-0"
                  title="Làm mới danh sách"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingRequests ? 'animate-spin text-amber-600' : ''}`} />
                </button>
              </div>
            </div>

            {/* Bulk Actions Alert / Toolbar right above table */}
            {selectedRequestIds.length > 0 && (
              <div className="bg-gradient-to-r from-emerald-950 via-slate-900 to-teal-950 text-white p-3.5 sm:p-4 rounded-2xl shadow-lg border border-emerald-700/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 animate-fade-in">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 rounded-xl bg-emerald-500 text-white text-xs flex items-center justify-center font-black shadow-xs shrink-0">
                    {selectedRequestIds.length}
                  </div>
                  <div>
                    <div className="text-xs font-extrabold text-white flex items-center space-x-2">
                      <span>Đã chọn {selectedRequestIds.length} đơn hàng</span>
                      <span className="px-2 py-0.5 bg-emerald-500/30 text-emerald-200 border border-emerald-400/30 rounded-full text-[10px] font-bold">
                        Thao tác hàng loạt
                      </span>
                    </div>
                    <p className="text-[11px] text-emerald-200/90 mt-0.5">
                      Duyệt hoặc từ chối đồng loạt cho tất cả các giao dịch đã chọn
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      setBulkAdminNote('Duyệt thanh toán hàng loạt bởi Admin');
                      setBulkActionType('approve');
                    }}
                    className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center space-x-1.5 cursor-pointer active:scale-98"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>Duyệt Hàng Loạt</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setBulkAdminNote('Từ chối hàng loạt do không hợp lệ');
                      setBulkActionType('reject');
                    }}
                    className="px-3.5 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center space-x-1.5 cursor-pointer active:scale-98"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    <span>Từ Chối Hàng Loạt</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedRequestIds([])}
                    className="p-2 bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-semibold rounded-xl transition-colors cursor-pointer"
                    title="Bỏ chọn tất cả"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Requests Table */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                      <th className="py-3 px-4 w-10 text-center">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                        />
                      </th>
                      <th className="py-3 px-4">Thời gian</th>
                      <th className="py-3 px-4">Mã đơn / Cú pháp</th>
                      <th className="py-3 px-4">Người dùng</th>
                      <th className="py-3 px-4 text-center">Loại Giao Dịch</th>
                      <th className="py-3 px-4">Số tiền</th>
                      <th className="py-3 px-4">Quy đổi / Nhận</th>
                      <th className="py-3 px-4 text-center">Trạng thái</th>
                      <th className="py-3 px-4 text-right">Thao tác</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-slate-100">
                    {loadingRequests ? (
                      <tr>
                        <td colSpan={9} className="py-12 text-center text-slate-400">
                          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-amber-500" />
                          <span>Đang tải danh sách đơn hàng...</span>
                        </td>
                      </tr>
                    ) : requests.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="py-12 text-center text-slate-400">
                          <div className="max-w-xs mx-auto space-y-1">
                            <Coins className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                            <p className="font-semibold text-slate-600">Không tìm thấy đơn hàng nào</p>
                            <p className="text-[11px] text-slate-400">Thử thay đổi bộ lọc hoặc từ khóa tìm kiếm</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      requests.map(req => {
                        const amountVal = req?.amountMoney ?? (req as any)?.amountVnd ?? (req as any)?.amount ?? 0;
                        const creditsVal = req?.creditsToGain ?? (req as any)?.credits ?? 0;
                        const pkgType = req.packageType || (req as any)?.type || (
                          req.packageName?.includes('SVIP') ? 'SVIP' :
                          req.packageName?.includes('VIP') ? 'VIP' : 'XU'
                        );
                        const code = req.paymentCode || req.transferContent || req.id;
                        const reqId = req.id || (req as any)._id;
                        const isSelected = selectedRequestIds.includes(reqId);

                        return (
                          <tr key={reqId} className={`transition-colors ${isSelected ? 'bg-emerald-50/40' : 'hover:bg-amber-50/20'}`}>
                            {/* Checkbox */}
                            <td className="py-3.5 px-4 text-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleSelectRow(reqId)}
                                className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                              />
                            </td>

                            {/* 1. Thời gian */}
                            <td className="py-3.5 px-4 whitespace-nowrap text-slate-600 font-medium">
                              <div className="flex items-center space-x-1.5">
                                <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                <span>{formatDateTime(req.createdAt)}</span>
                              </div>
                            </td>

                            {/* 2. Cú pháp / Mã đơn (Bold + Copy) */}
                            <td className="py-3.5 px-4 font-mono whitespace-nowrap">
                              <div className="flex items-center space-x-1.5">
                                <span className="font-extrabold text-slate-900 bg-slate-100 hover:bg-slate-200/80 px-2 py-0.5 rounded-md border border-slate-200 transition-colors">
                                  {code}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleCopy(code)}
                                  className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors cursor-pointer"
                                  title="Sao chép cú pháp / mã đơn"
                                >
                                  {copiedCode === code ? (
                                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                                  ) : (
                                    <Copy className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              </div>
                            </td>

                            {/* 3. Người dùng */}
                            <td className="py-3.5 px-4">
                              <div className="font-bold text-slate-900">{req.userName || 'Người dùng'}</div>
                              <div className="text-[11px] text-slate-500 font-medium">{req.userEmail}</div>
                            </td>

                            {/* 4. Loại Giao Dịch */}
                            <td className="py-3.5 px-4 text-center whitespace-nowrap">
                              {pkgType === 'SVIP' ? (
                                <span className="px-2.5 py-1 bg-rose-50 text-rose-700 border border-rose-200/80 font-bold text-[11px] rounded-lg inline-flex items-center space-x-1 shadow-2xs">
                                  <Gem className="w-3 h-3 text-rose-600" />
                                  <span>Nâng SVIP</span>
                                </span>
                              ) : pkgType === 'VIP' ? (
                                <span className="px-2.5 py-1 bg-purple-50 text-purple-700 border border-purple-200/80 font-bold text-[11px] rounded-lg inline-flex items-center space-x-1 shadow-2xs">
                                  <Crown className="w-3 h-3 text-purple-600" />
                                  <span>Nâng VIP</span>
                                </span>
                              ) : (
                                <span className="px-2.5 py-1 bg-amber-50 text-amber-800 border border-amber-200/80 font-bold text-[11px] rounded-lg inline-flex items-center space-x-1">
                                  <Coins className="w-3 h-3 text-amber-600" />
                                  <span>Nạp Xu</span>
                                </span>
                              )}
                            </td>

                            {/* 5. Số tiền (Màu xanh lá) */}
                            <td className="py-3.5 px-4 whitespace-nowrap font-extrabold text-emerald-700 text-[13px]">
                              {amountVal.toLocaleString('vi-VN')} VNĐ
                            </td>

                            {/* 6. Quy đổi / Nhận */}
                            <td className="py-3.5 px-4 whitespace-nowrap">
                              {pkgType === 'VIP' || pkgType === 'SVIP' ? (
                                <div className="space-y-0.5">
                                  <div className="font-bold text-slate-800 text-[11px]">
                                    {req.packageName || (pkgType === 'SVIP' ? 'Gói SVIP' : 'Gói VIP')}
                                  </div>
                                  {creditsVal > 0 && (
                                    <span className="px-1.5 py-0.5 bg-amber-50 text-amber-800 text-[10px] font-bold rounded border border-amber-200 inline-block">
                                      +{creditsVal.toLocaleString('vi-VN')} Xu
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="px-2 py-0.5 bg-amber-50 rounded-md text-amber-900 border border-amber-200 font-extrabold">
                                  +{creditsVal.toLocaleString('vi-VN')} Xu
                                </span>
                              )}
                            </td>

                            {/* 7. Trạng thái (Bao gồm WAITING_PAYMENT xám/xanh nhạt) */}
                            <td className="py-3.5 px-4 text-center whitespace-nowrap">
                              {req.status === 'WAITING_PAYMENT' ? (
                                <span className="px-2.5 py-1 bg-slate-100 text-slate-700 font-bold text-[11px] rounded-lg border border-slate-200 inline-flex items-center space-x-1">
                                  <Clock className="w-3 h-3 text-slate-500" />
                                  <span>Chờ thanh toán</span>
                                </span>
                              ) : req.status === 'PENDING' ? (
                                <span className="px-2.5 py-1 bg-amber-100 text-amber-900 font-bold text-[11px] rounded-lg border border-amber-200 inline-flex items-center space-x-1">
                                  <Clock className="w-3 h-3 text-amber-600" />
                                  <span>Chờ duyệt</span>
                                </span>
                              ) : req.status === 'APPROVED' ? (
                                <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 font-bold text-[11px] rounded-lg border border-emerald-200 inline-flex items-center space-x-1">
                                  <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                  <span>Đã duyệt</span>
                                </span>
                              ) : (
                                <span className="px-2.5 py-1 bg-rose-100 text-rose-800 font-bold text-[11px] rounded-lg border border-rose-200 inline-flex items-center space-x-1">
                                  <XCircle className="w-3 h-3 text-rose-600" />
                                  <span>Từ chối</span>
                                </span>
                              )}
                            </td>

                            {/* 8. Thao tác (Hiển thị nút Duyệt cho PENDING & WAITING_PAYMENT) */}
                            <td className="py-3.5 px-4 text-right space-x-1.5 whitespace-nowrap">
                              {(req.status === 'PENDING' || req.status === 'WAITING_PAYMENT') ? (
                                <div className="flex items-center justify-end space-x-1.5">
                                  <button
                                    type="button"
                                    disabled={processingId === req.id}
                                    onClick={() => handleApprove(req)}
                                    className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-2xs transition-all inline-flex items-center space-x-1 disabled:opacity-50 cursor-pointer"
                                    title="Duyệt đơn thanh toán"
                                  >
                                    {processingId === req.id ? (
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                    ) : (
                                      <CheckCircle2 className="w-3.5 h-3.5" />
                                    )}
                                    <span>Duyệt</span>
                                  </button>

                                  <button
                                    type="button"
                                    disabled={processingId === req.id}
                                    onClick={() => handleReject(req)}
                                    className="px-2 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-bold text-xs rounded-xl transition-all inline-flex items-center space-x-1 disabled:opacity-50 cursor-pointer"
                                    title="Từ chối đơn"
                                  >
                                    <XCircle className="w-3.5 h-3.5" />
                                    <span>Từ chối</span>
                                  </button>
                                </div>
                              ) : req.status === 'APPROVED' ? (
                                <span className="text-[11px] font-semibold text-emerald-600 inline-flex items-center space-x-1">
                                  <Check className="w-3.5 h-3.5" />
                                  <span>Hoàn tất</span>
                                </span>
                              ) : (
                                <span className="text-[11px] font-medium text-slate-400">
                                  Đã hủy
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Server-Side Pagination Bar */}
              {!loadingRequests && totalRecords > 0 && (
                <ServerPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  totalRecords={totalRecords}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={(newSize) => {
                    setPageSize(newSize);
                    setCurrentPage(1);
                  }}
                  itemLabel="đơn hàng"
                />
              )}
            </div>

            {/* Floating Bulk Actions Toolbar */}
            {selectedRequestIds.length > 0 && (
              <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center space-x-4 border border-slate-800 animate-slide-up">
                <div className="text-xs font-bold text-slate-200 flex items-center space-x-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-600 text-white text-[11px] flex items-center justify-center font-black">
                    {selectedRequestIds.length}
                  </span>
                  <span>Đã chọn {selectedRequestIds.length} đơn hàng</span>
                </div>

                <div className="h-4 w-px bg-slate-700" />

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => {
                      setBulkAdminNote('Duyệt thanh toán hàng loạt bởi Admin');
                      setBulkActionType('approve');
                    }}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl transition-all flex items-center space-x-1.5 cursor-pointer shadow-xs"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>Duyệt Hàng Loạt</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setBulkAdminNote('Từ chối hàng loạt do không hợp lệ');
                      setBulkActionType('reject');
                    }}
                    className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl transition-all flex items-center space-x-1.5 cursor-pointer shadow-xs"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    <span>Từ Chối Hàng Loạt</span>
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedRequestIds([])}
                  className="p-1 hover:bg-slate-800 text-slate-400 hover:text-white rounded-lg transition-colors ml-2 cursor-pointer"
                  title="Bỏ chọn"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* ==================== SUB-TAB 2: CÀI ĐẶT GÓI XU ==================== */}
      {activeSubTab === 'packages' && (
        <div className="space-y-6">
          {/* Header Banner */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-base font-extrabold text-slate-900 flex items-center space-x-2">
                <Package className="w-5 h-5 text-amber-500" />
                <span>Cấu Hình Gói Nạp Xu</span>
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Thiết lập giá VNĐ, tỷ lệ quy đổi Xu và các huy hiệu ưu đãi hiển thị cho người dùng
              </p>
            </div>

            <div className="flex items-center space-x-2 shrink-0">
              <button
                type="button"
                onClick={fetchPackages}
                className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors cursor-pointer"
                title="Làm mới"
              >
                <RefreshCw className={`w-4 h-4 ${loadingPackages ? 'animate-spin' : ''}`} />
              </button>

              <button
                type="button"
                onClick={() => handleOpenPackageModal()}
                className="px-4 py-2 bg-[#224783] hover:bg-[#1b3a6b] text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center space-x-1.5 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>Thêm Gói mới</span>
              </button>
            </div>
          </div>

          {/* Packages Table */}
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-2xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    <th className="py-3 px-4 text-center w-12">STT</th>
                    <th className="py-3 px-4">Tên Gói</th>
                    <th className="py-3 px-4 text-center">Phân Loại</th>
                    <th className="py-3 px-4">Giá VNĐ</th>
                    <th className="py-3 px-4">Quyền Lợi / Xu</th>
                    <th className="py-3 px-4">Huy hiệu (Tag)</th>
                    <th className="py-3 px-4 text-center">Kho (Stock)</th>
                    <th className="py-3 px-4 text-center">Trạng Thái</th>
                    <th className="py-3 px-4 text-right">Thao Tác</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {loadingPackages ? (
                    <tr>
                      <td colSpan={9} className="py-12 text-center text-slate-400">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-amber-500" />
                        <span>Đang tải danh sách Gói...</span>
                      </td>
                    </tr>
                  ) : packages.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="py-12 text-center text-slate-400">
                        Chưa có Gói nào. Vui lòng bấm "+ Thêm Gói mới" để tạo gói.
                      </td>
                    </tr>
                  ) : (
                    packages.map((pkg, index) => {
                      const priceVal = pkg?.price ?? (pkg as any)?.amountVnd ?? 0;
                      const creditsVal = pkg?.credits ?? 0;
                      const isVip = pkg.type === 'VIP';
                      const isSvip = pkg.type === 'SVIP';
                      const maxQty = Number(pkg.maxQuantity || 0);
                      const soldQty = Number(pkg.soldQuantity || 0);
                      const isOutOfStock = maxQty > 0 && soldQty >= maxQty;
                      const percent = maxQty > 0 ? Math.min(100, Math.max(0, Math.round((soldQty / maxQty) * 100))) : 0;

                      return (
                        <tr key={pkg.id || pkg.packageId} className="hover:bg-slate-50 transition-colors">
                          <td className="py-3.5 px-4 text-center font-bold text-slate-400">
                            {pkg.order || index + 1}
                          </td>

                          <td className="py-3.5 px-4">
                            <div className="font-extrabold text-slate-900 text-sm">{pkg.name}</div>
                            <div className="text-[11px] text-slate-500 line-clamp-1">{pkg.description || 'Chưa có mô tả'}</div>
                          </td>

                          <td className="py-3.5 px-4 text-center">
                            {isSvip ? (
                              <span className="px-2.5 py-1 bg-gradient-to-r from-rose-600 to-amber-500 text-white font-extrabold text-[10px] rounded-lg shadow-2xs">
                                SVIP
                              </span>
                            ) : isVip ? (
                              <span className="px-2.5 py-1 bg-gradient-to-r from-amber-500 to-yellow-500 text-amber-950 font-extrabold text-[10px] rounded-lg shadow-2xs">
                                VIP
                              </span>
                            ) : (
                              <span className="px-2.5 py-1 bg-slate-100 text-slate-700 font-bold text-[10px] rounded-lg border border-slate-200">
                                XU
                              </span>
                            )}
                          </td>

                          <td className="py-3.5 px-4 font-extrabold text-emerald-700 text-sm">
                            {priceVal === 0 ? (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-900 rounded-md border border-amber-200">
                                MIỄN PHÍ (0 VNĐ)
                              </span>
                            ) : (
                              `${priceVal.toLocaleString('vi-VN')} VNĐ`
                            )}
                          </td>

                          <td className="py-3.5 px-4 font-extrabold text-amber-800 text-sm">
                            {isVip || isSvip ? (
                              <div className="space-y-0.5">
                                <span className={`px-2 py-0.5 rounded-md text-xs font-extrabold block w-fit ${
                                  isSvip ? 'bg-rose-100 text-rose-900 border border-rose-200' : 'bg-amber-100 text-amber-900 border border-amber-200'
                                }`}>
                                  {pkg.durationDays || (isSvip ? 365 : 30)} ngày
                                </span>
                                {creditsVal > 0 && (
                                  <span className="text-[10px] font-bold text-amber-700 block">
                                    +{creditsVal.toLocaleString('vi-VN')} Xu
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="px-2 py-0.5 bg-amber-100 text-amber-900 rounded-md border border-amber-200">
                                +{creditsVal.toLocaleString('vi-VN')} Xu
                              </span>
                            )}
                          </td>

                          <td className="py-3.5 px-4">
                            {pkg.badgeText ? (
                              <span className="px-2 py-0.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-extrabold rounded-full uppercase shadow-2xs">
                                {pkg.badgeText}
                              </span>
                            ) : (
                              <span className="text-slate-300 text-[11px] italic">Không có</span>
                            )}
                          </td>

                          {/* Cột Kho (Stock) */}
                          <td className="py-3.5 px-4 text-center whitespace-nowrap min-w-[140px]">
                            {maxQty === 0 ? (
                              <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 font-extrabold text-[11px] rounded-lg inline-flex items-center space-x-1 shadow-2xs">
                                <span>Vô hạn</span>
                              </span>
                            ) : (
                              <div className="space-y-1 inline-block text-left w-full min-w-[130px] max-w-[160px]">
                                <div className="flex items-center justify-between text-[11px]">
                                  <span className="font-semibold text-slate-700">
                                    Đã phát: <span className="font-extrabold text-slate-900">{soldQty}</span> / {maxQty}
                                  </span>
                                  {isOutOfStock && (
                                    <span className="text-[10px] font-black text-rose-600 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded ml-1">
                                      Hết hàng
                                    </span>
                                  )}
                                </div>
                                <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden border border-slate-200/60">
                                  <div
                                    className={`h-full rounded-full transition-all duration-300 ${
                                      isOutOfStock ? 'bg-rose-500' : 'bg-emerald-500'
                                    }`}
                                    style={{ width: `${percent}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </td>

                          <td className="py-3.5 px-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleTogglePackageActive(pkg)}
                              className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-all inline-flex items-center space-x-1 cursor-pointer ${
                                pkg.isActive
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                  : 'bg-slate-100 text-slate-500 border-slate-200 hover:bg-slate-200'
                              }`}
                              title="Bấm để Đổi trạng thái"
                            >
                              <span className={`w-2 h-2 rounded-full ${pkg.isActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
                              <span>{pkg.isActive ? 'Đang bật' : 'Đang tắt'}</span>
                            </button>
                          </td>

                          <td className="py-3.5 px-4 text-right space-x-1.5">
                            <button
                              type="button"
                              onClick={() => handleOpenPackageModal(pkg)}
                              className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all inline-flex items-center space-x-1"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                              <span>Sửa</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => handleDeletePackage(pkg)}
                              className="px-2.5 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 font-bold text-xs rounded-xl transition-all inline-flex items-center space-x-1"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              <span>Xóa</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ==================== SUB-TAB 3: CÀI ĐẶT NGÂN HÀNG ==================== */}
      {activeSubTab === 'bank' && (
        <div className="space-y-6">
          {/* Header Banner */}
          <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h2 className="text-base font-extrabold text-slate-900 flex items-center space-x-2">
                <Building2 className="w-5 h-5 text-amber-500" />
                <span>Cấu Hình Ngân Hàng Nhận Tiền (VietQR)</span>
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                Cập nhật thông tin tài khoản ngân hàng hiển thị trên mã QR khi người dùng thực hiện nạp Xu
              </p>
            </div>

            <button
              type="button"
              onClick={fetchBankInfo}
              className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors shrink-0"
              title="Làm mới"
            >
              <RefreshCw className={`w-4 h-4 ${loadingBankInfo ? 'animate-spin' : ''}`} />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Form Column */}
            <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs space-y-5">
              <div className="flex items-center space-x-2 pb-3 border-b border-slate-100">
                <ShieldCheck className="w-5 h-5 text-indigo-600" />
                <h3 className="text-sm font-extrabold text-slate-900">Thông tin Tài khoản VietQR</h3>
              </div>

              {loadingBankInfo ? (
                <div className="py-12 text-center text-slate-400">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-amber-500" />
                  <span>Đang tải cấu hình ngân hàng...</span>
                </div>
              ) : (
                <form onSubmit={handleSaveBankInfo} className="space-y-4">
                  {/* Quick Pick Bank */}
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">
                      Gợi ý chọn nhanh Ngân hàng phổ biến:
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {POPULAR_BANKS.map((b) => (
                        <button
                          key={b.bankId}
                          type="button"
                          onClick={() => setBankConfig(prev => ({ ...prev, bankName: b.bankName, bankId: b.bankId }))}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold border transition-all ${
                            bankConfig.bankId === b.bankId
                              ? 'bg-amber-500 text-white border-amber-600 shadow-2xs'
                              : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                          }`}
                        >
                          {b.bankId}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        1. Tên Ngân Hàng <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={bankConfig.bankName}
                        onChange={e => setBankConfig({ ...bankConfig, bankName: e.target.value })}
                        placeholder="Ví dụ: MBBank (Ngân hàng Quân Đội)"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-semibold text-slate-900 focus:bg-white focus:border-amber-500 outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        2. Mã VietQR Bank ID <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={bankConfig.bankId}
                        onChange={e => setBankConfig({ ...bankConfig, bankId: e.target.value.toUpperCase() })}
                        placeholder="Ví dụ: MB, VCB, TCB, ICB, VPB..."
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-mono font-bold text-slate-900 focus:bg-white focus:border-amber-500 outline-none uppercase"
                      />
                      <span className="text-[10px] text-slate-400 mt-1 block">
                        Mã ngắn định danh ngân hàng theo VietQR (MB, VCB, TCB, BIDV, ICB...)
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        3. Số Tài Khoản <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={bankConfig.accountNo}
                        onChange={e => setBankConfig({ ...bankConfig, accountNo: e.target.value })}
                        placeholder="Ví dụ: 0988888888"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-mono font-extrabold text-indigo-900 focus:bg-white focus:border-amber-500 outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1">
                        4. Tên Chủ Tài Khoản <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={bankConfig.accountName}
                        onChange={e => setBankConfig({ ...bankConfig, accountName: e.target.value.toUpperCase() })}
                        placeholder="Ví dụ: VO THI MINH HAN"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-xs font-bold text-slate-900 focus:bg-white focus:border-amber-500 outline-none uppercase"
                      />
                      <span className="text-[10px] text-slate-400 mt-1 block">
                        Viết HOA không dấu giống trên thẻ ngân hàng
                      </span>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-slate-100 flex items-center justify-end">
                    <button
                      type="submit"
                      disabled={isSavingBankInfo}
                      className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center space-x-2 disabled:opacity-50"
                    >
                      {isSavingBankInfo ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Đang lưu...</span>
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          <span>Lưu Cấu Hình Ngân Hàng</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Live Preview Card */}
            <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white p-6 rounded-2xl shadow-lg border border-slate-800 flex flex-col justify-between space-y-4">
              <div>
                <div className="flex items-center justify-between pb-3 border-b border-white/10">
                  <div className="flex items-center space-x-2">
                    <QrCode className="w-5 h-5 text-amber-400" />
                    <span className="text-xs font-extrabold uppercase tracking-wider text-amber-300">Xem trước VietQR</span>
                  </div>
                  <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-[10px] font-bold rounded-full border border-emerald-500/30">
                    Live Preview
                  </span>
                </div>

                <div className="mt-4 space-y-3 text-xs">
                  <div>
                    <span className="text-[10px] text-slate-400 block uppercase font-bold">Ngân hàng nhận</span>
                    <span className="font-extrabold text-white text-sm">{bankConfig.bankName || 'Chưa thiết lập'}</span>
                  </div>

                  <div>
                    <span className="text-[10px] text-slate-400 block uppercase font-bold">Mã VietQR ID</span>
                    <span className="font-mono font-bold text-amber-400">{bankConfig.bankId || 'MB'}</span>
                  </div>

                  <div>
                    <span className="text-[10px] text-slate-400 block uppercase font-bold">Số tài khoản</span>
                    <span className="font-mono font-extrabold text-emerald-400 text-base">{bankConfig.accountNo || '0000000000'}</span>
                  </div>

                  <div>
                    <span className="text-[10px] text-slate-400 block uppercase font-bold">Chủ tài khoản</span>
                    <span className="font-bold text-slate-200">{bankConfig.accountName || 'VO THI MINH HAN'}</span>
                  </div>
                </div>
              </div>

              {/* VietQR Sample Image Preview */}
              <div className="p-3 bg-white/10 rounded-xl backdrop-blur-xs border border-white/10 text-center">
                <img
                  src={`https://img.vietqr.io/image/${bankConfig.bankId || 'MB'}-${bankConfig.accountNo || '0388888888'}-compact.png?amount=100000&addInfo=NGS%20SAMPLE&accountName=${encodeURIComponent(bankConfig.accountName || 'VO THI MINH HAN')}`}
                  alt="VietQR Preview"
                  className="w-36 h-36 object-contain mx-auto rounded-lg bg-white p-1 border border-white/20 shadow-xs"
                />
                <span className="text-[10px] text-slate-300 block mt-2 italic">
                  Hình ảnh mẫu VietQR tự động sinh cho người dùng
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ==================== SUB-TAB 4: CÀI ĐẶT COUPON (MÃ GIẢM GIÁ) ==================== */}
      {activeSubTab === 'coupons' && (() => {
        const filteredCoupons = coupons.filter(c => {
          if (!couponSearch.trim()) return true;
          const q = couponSearch.toLowerCase().trim();
          return c.code.toLowerCase().includes(q) || (c.description && c.description.toLowerCase().includes(q));
        });

        return (
          <div className="space-y-4">
            {/* Header & Quick stats / Search bar */}
            <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center border border-amber-200 shadow-2xs">
                  <Tag className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-slate-900">Quản lý Mã Khuyến Mãi & Giảm Giá</h3>
                  <p className="text-xs text-slate-500">
                    Tạo và quản lý các mã coupon ưu đãi khi người dùng nạp Xu hoặc nâng cấp VIP/SVIP
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <div className="relative min-w-[220px]">
                  <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={couponSearch}
                    onChange={e => setCouponSearch(e.target.value)}
                    placeholder="Tìm theo mã coupon, mô tả..."
                    className="w-full pl-8 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:bg-white focus:border-amber-500 outline-none transition-all"
                  />
                  {couponSearch && (
                    <button
                      type="button"
                      onClick={() => setCouponSearch('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={fetchCoupons}
                  disabled={loadingCoupons}
                  className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition-colors cursor-pointer"
                  title="Làm mới danh sách"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingCoupons ? 'animate-spin text-amber-600' : ''}`} />
                </button>
              </div>
            </div>

            {/* Coupons Table */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
              {loadingCoupons ? (
                <div className="py-16 flex flex-col items-center justify-center space-y-3 text-slate-500">
                  <Loader2 className="w-7 h-7 text-amber-500 animate-spin" />
                  <p className="text-xs font-semibold">Đang tải danh sách mã giảm giá...</p>
                </div>
              ) : filteredCoupons.length === 0 ? (
                <div className="py-16 flex flex-col items-center justify-center space-y-3 text-slate-400">
                  <div className="w-14 h-14 rounded-2xl bg-amber-50 text-amber-400 flex items-center justify-center border border-amber-100 shadow-2xs">
                    <Tag className="w-7 h-7" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-bold text-slate-700">
                      {couponSearch ? 'Không tìm thấy mã giảm giá phù hợp' : 'Chưa có mã giảm giá nào'}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {couponSearch ? 'Vui lòng thử tìm kiếm với từ khóa khác' : 'Nhấn nút "Thêm Mã Giảm Giá" để tạo coupon ưu đãi đầu tiên'}
                    </p>
                  </div>
                  {!couponSearch && (
                    <button
                      type="button"
                      onClick={() => handleOpenCouponModal()}
                      className="mt-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center space-x-1.5 cursor-pointer"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Thêm Mã Giảm Giá</span>
                    </button>
                  )}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider">
                        <th className="py-3 px-4">Mã Coupon</th>
                        <th className="py-3 px-4">% Giảm</th>
                        <th className="py-3 px-4">Giới Hạn Lượt Dùng</th>
                        <th className="py-3 px-4">Thời Hạn</th>
                        <th className="py-3 px-4 text-center">Trạng Thái</th>
                        <th className="py-3 px-4 text-right">Thao Tác</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-xs">
                      {filteredCoupons.map((coupon) => {
                        const couponId = coupon.id || coupon._id || '';
                        const isExpired = coupon.expiresAt ? new Date(coupon.expiresAt).getTime() < Date.now() : false;
                        const isLimitReached = coupon.usageLimit > 0 && (coupon.usageCount || 0) >= coupon.usageLimit;
                        const isToggling = togglingCouponId === couponId;

                        return (
                          <tr key={couponId} className="hover:bg-slate-50/80 transition-colors">
                            {/* Cột 1: Mã Coupon */}
                            <td className="py-3 px-4">
                              <div className="flex flex-col space-y-1">
                                <div className="flex items-center space-x-2">
                                  <span className="font-mono font-black text-xs px-2.5 py-1 bg-amber-50 text-amber-900 rounded-lg border border-amber-200 shadow-2xs inline-flex items-center space-x-1">
                                    <Tag className="w-3 h-3 text-amber-600" />
                                    <span>{coupon.code}</span>
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => handleCopy(coupon.code)}
                                    className="p-1 text-slate-400 hover:text-amber-600 hover:bg-slate-100 rounded-md transition-colors cursor-pointer"
                                    title="Sao chép mã"
                                  >
                                    {copiedCode === coupon.code ? (
                                      <CheckCheck className="w-3.5 h-3.5 text-emerald-600" />
                                    ) : (
                                      <Copy className="w-3.5 h-3.5" />
                                    )}
                                  </button>
                                </div>
                                {coupon.description && (
                                  <span className="text-[11px] text-slate-500 line-clamp-1">
                                    {coupon.description}
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Cột 2: % Giảm */}
                            <td className="py-3 px-4">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-black bg-emerald-50 text-emerald-700 border border-emerald-200 shadow-2xs">
                                <Percent className="w-3 h-3 mr-0.5 text-emerald-600" />
                                <span>Giảm {coupon.discountPercent}%</span>
                              </span>
                            </td>

                            {/* Cột 3: Giới hạn lượt dùng */}
                            <td className="py-3 px-4">
                              <div className="flex items-center space-x-1.5">
                                <span className="font-extrabold text-slate-800">
                                  {coupon.usageCount || 0}
                                </span>
                                <span className="text-slate-400">/</span>
                                {coupon.usageLimit > 0 ? (
                                  <span className={`font-semibold ${isLimitReached ? 'text-rose-600 font-bold' : 'text-slate-600'}`}>
                                    {coupon.usageLimit} lượt
                                    {isLimitReached && (
                                      <span className="ml-1 text-[10px] text-rose-500 font-bold bg-rose-50 px-1 py-0.5 rounded border border-rose-200">
                                        Đã hết
                                      </span>
                                    )}
                                  </span>
                                ) : (
                                  <span className="text-emerald-600 font-semibold bg-emerald-50 px-1.5 py-0.5 rounded text-[11px] border border-emerald-100">
                                    Không giới hạn
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Cột 4: Thời hạn */}
                            <td className="py-3 px-4">
                              {coupon.expiresAt ? (
                                <div className="flex items-center space-x-1.5">
                                  <Calendar className={`w-3.5 h-3.5 ${isExpired ? 'text-rose-500' : 'text-slate-400'}`} />
                                  <span className={`font-medium ${isExpired ? 'text-rose-600 font-bold' : 'text-slate-700'}`}>
                                    {formatDateOnly(coupon.expiresAt)}
                                  </span>
                                  {isExpired && (
                                    <span className="text-[10px] text-rose-600 font-bold bg-rose-50 px-1.5 py-0.5 rounded border border-rose-200">
                                      Hết hạn
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md text-[11px] font-semibold">
                                  Vô thời hạn
                                </span>
                              )}
                            </td>

                            {/* Cột 5: Trạng thái (Toggle/Switch) */}
                            <td className="py-3 px-4 text-center">
                              <button
                                type="button"
                                onClick={() => handleToggleCouponActive(coupon)}
                                disabled={isToggling}
                                className="inline-flex items-center cursor-pointer transition-transform hover:scale-105 active:scale-95 disabled:opacity-50"
                                title={coupon.isActive ? "Nhấn để tạm ngưng" : "Nhấn để kích hoạt"}
                              >
                                {isToggling ? (
                                  <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
                                ) : coupon.isActive ? (
                                  <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    <ToggleRight className="w-4 h-4 text-emerald-600" />
                                    <span>Đang bật</span>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-500 border border-slate-200">
                                    <ToggleLeft className="w-4 h-4 text-slate-400" />
                                    <span>Tạm tắt</span>
                                  </span>
                                )}
                              </button>
                            </td>

                            {/* Cột 6: Thao tác (Sửa, Xóa) */}
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end space-x-1.5">
                                <button
                                  type="button"
                                  onClick={() => handleOpenCouponModal(coupon)}
                                  className="p-1.5 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
                                  title="Chỉnh sửa mã giảm giá"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </button>

                                <button
                                  type="button"
                                  onClick={() => handleDeleteCoupon(coupon)}
                                  className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                                  title="Xóa mã giảm giá"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ==================== MODAL FORM: THÊM / SỬA GÓI NẠP ==================== */}
      {showPackageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 relative space-y-5 overflow-hidden">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center space-x-2.5">
                <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-900 flex items-center justify-center font-bold shadow-2xs">
                  <Package className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <h2 className="text-base font-extrabold text-slate-900">
                    {editingPackage ? 'Cập Nhật Gói Nạp' : 'Thêm Gói Nạp Mới'}
                  </h2>
                  <p className="text-xs text-slate-500">
                    Cấu hình tên gói, phân loại (Xu / VIP / SVIP), giá tiền và thời hạn
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowPackageModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSavePackage} className="space-y-4">
              {/* Tên Gói & Phân Loại Gói */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Tên Gói <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={pkgFormData.name}
                  onChange={e => setPkgFormData({ ...pkgFormData, name: e.target.value })}
                  placeholder={
                    pkgFormData.type === 'SVIP'
                      ? "Ví dụ: Gói SVIP 1 Năm"
                      : pkgFormData.type === 'VIP'
                      ? "Ví dụ: Gói VIP 1 Tháng"
                      : "Ví dụ: Gói Xu Phổ Biến"
                  }
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 focus:bg-white focus:border-amber-500 outline-none"
                />
              </div>

              {/* Loại Gói & (Conditional) Số Ngày Hiệu Lực */}
              <div className={`grid ${pkgFormData.type !== 'XU' ? 'grid-cols-2' : 'grid-cols-1'} gap-3 transition-all`}>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Loại Gói <span className="text-rose-500">*</span>
                  </label>
                  <select
                    value={pkgFormData.type}
                    onChange={e => {
                      const newType = e.target.value as 'XU' | 'VIP' | 'SVIP';
                      setPkgFormData(prev => ({
                        ...prev,
                        type: newType,
                        durationDays: newType === 'XU' ? 0 : (prev.durationDays && prev.durationDays > 0 ? prev.durationDays : (newType === 'SVIP' ? 365 : 30))
                      }));
                    }}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-900 focus:bg-white focus:border-amber-500 outline-none cursor-pointer"
                  >
                    <option value="XU">🪙 Gói Nạp Xu (XU)</option>
                    <option value="VIP">👑 Gói VIP (VIP)</option>
                    <option value="SVIP">💎 Gói Siêu VIP (SVIP)</option>
                  </select>
                </div>

                {/* Conditional Rendering: Chỉ hiển thị Số Ngày Hiệu Lực khi là VIP hoặc SVIP */}
                {pkgFormData.type !== 'XU' && (
                  <div className="animate-fade-in">
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      Số Ngày Hiệu Lực <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="number"
                      required
                      min={1}
                      value={pkgFormData.durationDays}
                      onChange={e => setPkgFormData({ ...pkgFormData, durationDays: Math.max(1, Number(e.target.value)) })}
                      placeholder="VD: 30, 90, 365"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-bold text-indigo-700 focus:bg-white focus:border-indigo-500 outline-none"
                    />
                    <span className="text-[10px] text-slate-400 block mt-0.5">Số ngày VIP sau khi duyệt</span>
                  </div>
                )}
              </div>

              {/* Giá Tiền & Số Xu */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Giá Tiền (VNĐ) <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="number"
                    required
                    min={0}
                    step={1000}
                    value={pkgFormData.price}
                    onChange={e => setPkgFormData({ ...pkgFormData, price: Number(e.target.value) })}
                    placeholder="100000"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-extrabold text-emerald-700 focus:bg-white focus:border-amber-500 outline-none"
                  />
                  <span className="text-[10px] text-slate-400 block mt-0.5">Nhập 0 nếu là gói Miễn phí (0 VNĐ)</span>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    {pkgFormData.type === 'XU' ? (
                      <>Số Xu Nhận <span className="text-rose-500">*</span></>
                    ) : (
                      <>Số Xu Tặng Kèm</>
                    )}
                  </label>
                  <input
                    type="number"
                    required={pkgFormData.type === 'XU'}
                    min={0}
                    value={pkgFormData.credits}
                    onChange={e => setPkgFormData({ ...pkgFormData, credits: Number(e.target.value) })}
                    placeholder={pkgFormData.type === 'XU' ? "1000" : "0 (hoặc số Xu tặng)"}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-extrabold text-amber-800 focus:bg-white focus:border-amber-500 outline-none"
                  />
                  <span className="text-[10px] text-slate-400 block mt-0.5">
                    {pkgFormData.type === 'XU' ? 'Số Xu cộng vào tài khoản' : 'Để 0 nếu không tặng thêm Xu'}
                  </span>
                </div>
              </div>

              {/* Huy Hiệu Tag, Thứ Tự Hiển Thị & Giới Hạn Kho */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Huy Hiệu / Tag
                  </label>
                  <input
                    type="text"
                    value={pkgFormData.badgeText}
                    onChange={e => setPkgFormData({ ...pkgFormData, badgeText: e.target.value })}
                    placeholder="VD: HOT, TIẾT KIỆM, VIP..."
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 focus:bg-white focus:border-amber-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Thứ Tự Hiển Thị
                  </label>
                  <input
                    type="number"
                    min={1}
                    value={pkgFormData.order}
                    onChange={e => setPkgFormData({ ...pkgFormData, order: Number(e.target.value) })}
                    placeholder="1"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 focus:bg-white focus:border-amber-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Giới Hạn Kho (maxQuantity)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={pkgFormData.maxQuantity}
                    onChange={e => setPkgFormData({ ...pkgFormData, maxQuantity: Math.max(0, Number(e.target.value)) })}
                    placeholder="Nhập 0 nếu không muốn giới hạn"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 focus:bg-white focus:border-amber-500 outline-none"
                  />
                  <span className="text-[10px] text-slate-400 block mt-0.5">
                    Nhập 0 nếu không muốn giới hạn
                  </span>
                </div>
              </div>

              {/* Mô Tả Gói */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Mô Tả Gói
                </label>
                <textarea
                  rows={2}
                  value={pkgFormData.description}
                  onChange={e => setPkgFormData({ ...pkgFormData, description: e.target.value })}
                  placeholder="Mô tả quyền lợi, ưu đãi đặc quyền..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs font-medium text-slate-900 focus:bg-white focus:border-amber-500 outline-none"
                />
              </div>

              <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={pkgFormData.isActive}
                    onChange={e => setPkgFormData({ ...pkgFormData, isActive: e.target.checked })}
                    className="w-4 h-4 text-amber-500 rounded border-slate-300 focus:ring-amber-400"
                  />
                  <span className="text-xs font-bold text-slate-700">Cho phép hiển thị người dùng</span>
                </label>

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowPackageModal(false)}
                    className="py-2 px-3 border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold text-xs rounded-xl transition-colors cursor-pointer"
                  >
                    Hủy bỏ
                  </button>

                  <button
                    type="submit"
                    disabled={isSavingPackage}
                    className="py-2 px-4 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center space-x-1.5 disabled:opacity-50 cursor-pointer"
                  >
                    {isSavingPackage ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Đang lưu...</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        <span>Lưu Gói Nạp</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ==================== MODAL FORM: THÊM / SỬA MÃ GIẢM GIÁ (COUPON) ==================== */}
      {showCouponModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 relative space-y-5 overflow-hidden">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center space-x-2.5">
                <div className="w-10 h-10 rounded-2xl bg-amber-100 text-amber-900 flex items-center justify-center font-bold shadow-2xs">
                  <Tag className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <h2 className="text-base font-extrabold text-slate-900">
                    {editingCoupon ? 'Cập Nhật Mã Giảm Giá' : 'Thêm Mã Giảm Giá Mới'}
                  </h2>
                  <p className="text-xs text-slate-500">
                    Thiết lập mã coupon, tỷ lệ giảm giá, giới hạn lượt dùng và thời hạn
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowCouponModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSaveCoupon} className="space-y-4">
              {/* Mã Coupon & Phần trăm giảm */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Mã Coupon <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={couponFormData.code}
                    onChange={e => setCouponFormData({ ...couponFormData, code: e.target.value.toUpperCase() })}
                    placeholder="VD: GIAOVIEN20"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-mono font-black uppercase text-amber-900 focus:bg-white focus:border-amber-500 outline-none"
                  />
                  <span className="text-[10px] text-slate-400 mt-1 block">
                    Tự động viết hoa, không dấu cách
                  </span>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Phần Trăm Giảm (%) <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      required
                      min={0}
                      max={100}
                      step={1}
                      value={couponFormData.discountPercent}
                      onChange={e => setCouponFormData({ ...couponFormData, discountPercent: Number(e.target.value) })}
                      placeholder="VD: 20"
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-3.5 pr-8 py-2 text-xs font-mono font-extrabold text-emerald-700 focus:bg-white focus:border-amber-500 outline-none"
                    />
                    <Percent className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />
                  </div>
                  <span className="text-[10px] text-slate-400 mt-1 block">
                    Nhập từ 0 đến 100%
                  </span>
                </div>
              </div>

              {/* Giới hạn lượt dùng & Ngày hết hạn */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Số Lượng Giới Hạn
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={couponFormData.usageLimit}
                    onChange={e => setCouponFormData({ ...couponFormData, usageLimit: Math.max(0, parseInt(e.target.value) || 0) })}
                    placeholder="0 = Không giới hạn"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 focus:bg-white focus:border-amber-500 outline-none"
                  />
                  <span className="text-[10px] text-slate-400 mt-1 block">
                    Điền 0 nếu không giới hạn số lượt sử dụng
                  </span>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Thời Hạn Hết Hạn
                  </label>
                  <input
                    type="date"
                    value={couponFormData.expiresAt}
                    onChange={e => setCouponFormData({ ...couponFormData, expiresAt: e.target.value })}
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-semibold text-slate-900 focus:bg-white focus:border-amber-500 outline-none"
                  />
                  <span className="text-[10px] text-slate-400 mt-1 block">
                    Để trống nếu có hiệu lực vô thời hạn
                  </span>
                </div>
              </div>

              {/* Mô Tả Ghi Chú */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Mô Tả / Tên Chương Trình (Tùy chọn)
                </label>
                <textarea
                  rows={2}
                  value={couponFormData.description}
                  onChange={e => setCouponFormData({ ...couponFormData, description: e.target.value })}
                  placeholder="Ví dụ: Ưu đãi 20% nhân dịp khai giảng năm học mới..."
                  className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2 text-xs font-medium text-slate-900 focus:bg-white focus:border-amber-500 outline-none resize-none"
                />
              </div>

              {/* Checkbox Trạng Thái & Actions */}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={couponFormData.isActive}
                    onChange={e => setCouponFormData({ ...couponFormData, isActive: e.target.checked })}
                    className="w-4 h-4 text-amber-500 rounded border-slate-300 focus:ring-amber-400 cursor-pointer"
                  />
                  <span className="text-xs font-bold text-slate-700">Kích hoạt áp dụng ngay</span>
                </label>

                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowCouponModal(false)}
                    className="py-2 px-3 border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold text-xs rounded-xl transition-colors cursor-pointer"
                  >
                    Hủy bỏ
                  </button>

                  <button
                    type="submit"
                    disabled={isSavingCoupon}
                    className="py-2 px-4 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-xl shadow-xs transition-all flex items-center space-x-1.5 disabled:opacity-50 cursor-pointer"
                  >
                    {isSavingCoupon ? (
                      <>
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        <span>Đang lưu...</span>
                      </>
                    ) : (
                      <>
                        <Check className="w-3.5 h-3.5" />
                        <span>Lưu Mã Giảm Giá</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Action Confirmation Modal */}
      {bulkActionType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-slate-100 relative space-y-4">
            {/* Header */}
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-2xl ${bulkActionType === 'approve' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'} flex items-center justify-center shrink-0 shadow-xs`}>
                {bulkActionType === 'approve' ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
              </div>
              <div>
                <h3 className="text-sm font-extrabold text-slate-900">
                  {bulkActionType === 'approve' ? 'Xác nhận Duyệt Hàng Loạt' : 'Xác nhận Từ Chối Hàng Loạt'}
                </h3>
                <p className="text-xs text-slate-500">
                  Thao tác trên <span className="font-bold text-slate-800">{selectedRequestIds.length}</span> đơn hàng đã chọn
                </p>
              </div>
            </div>

            {/* Banner */}
            <div className={`p-3.5 rounded-2xl border ${bulkActionType === 'approve' ? 'bg-emerald-50/70 border-emerald-200/80 text-emerald-900' : 'bg-rose-50/70 border-rose-200/80 text-rose-900'}`}>
              <div className="text-xs font-semibold leading-relaxed">
                {bulkActionType === 'approve'
                  ? `Hệ thống sẽ tự động kích hoạt gói VIP hoặc cộng Xu tương ứng cho tất cả ${selectedRequestIds.length} tài khoản người dùng được chọn.`
                  : `Hệ thống sẽ chuyển trạng thái ${selectedRequestIds.length} đơn hàng sang Đã Hủy / Từ chối.`
                }
              </div>
            </div>

            {/* Input adminNote */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                Ghi chú / Lý do (Tùy chọn):
              </label>
              <input
                type="text"
                value={bulkAdminNote}
                onChange={e => setBulkAdminNote(e.target.value)}
                placeholder={bulkActionType === 'approve' ? 'Ví dụ: Đã nhận tiền qua ngân hàng...' : 'Ví dụ: Sai cú pháp, không nhận được tiền...'}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl py-2 px-3 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                disabled={isProcessingBulk}
                onClick={() => setBulkActionType(null)}
                className="py-2 px-3 border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold text-xs rounded-xl transition-colors cursor-pointer"
              >
                Hủy bỏ
              </button>

              <button
                type="button"
                disabled={isProcessingBulk}
                onClick={handleExecuteBulkAction}
                className={`py-2 px-4 font-bold text-xs rounded-xl shadow-xs transition-all flex items-center space-x-1.5 disabled:opacity-50 text-white cursor-pointer ${
                  bulkActionType === 'approve'
                    ? 'bg-emerald-600 hover:bg-emerald-700'
                    : 'bg-rose-600 hover:bg-rose-700'
                }`}
              >
                {isProcessingBulk ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span>Đang xử lý...</span>
                  </>
                ) : bulkActionType === 'approve' ? (
                  <>
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>Xác nhận Duyệt ({selectedRequestIds.length})</span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-3.5 h-3.5" />
                    <span>Xác nhận Từ chối ({selectedRequestIds.length})</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Dialog */}
      <CustomDialog
        isOpen={dialog.isOpen}
        type={dialog.type}
        title={dialog.title}
        message={dialog.message}
        onClose={() => setDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={dialog.onConfirm}
      />
    </div>
  );
};

export default TopUpApproval;
